import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logActivity } from '@/lib/auditLog';
import { generateClientDealNumber } from '@/lib/dealNumbers';
import { canCreateDealForProperty } from '@/lib/deals';

export function useAgentDeals() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['deals', 'agent', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .eq('agent_id', user!.id)
        .order('submitted_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });
}

export function useAgentDeal(dealId: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['deals', 'agent', dealId],
    enabled: !!dealId && !!user,
    queryFn: async () => {
      const { data: deal, error: dealError } = await supabase
        .from('deals')
        .select('*')
        .eq('id', dealId!)
        .single();
      if (dealError) throw dealError;

      // If accepted, fetch related matter and stages
      let matter = null;
      let stages = null;
      if (deal.status === 'accepted' || deal.status === 'in-progress') {
        const { data: matterData } = await supabase
          .from('matters')
          .select('*')
          .eq('deal_id', deal.id)
          .maybeSingle();
        matter = matterData;

        if (matter) {
          const { data: stagesData } = await supabase.rpc('get_matter_stages_for_client', {
            p_matter_id: matter.id,
          });
          stages = stagesData;
        }
      }

      return { deal, matter, stages };
    },
  });
}

export function useSubmitDeal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dealData: {
      property_id: string;
      property_title: string;
      property_address: string;
      property_state: string;
      property_lga?: string;
      property_type?: string;
      deal_type?: string;
      estimated_value: number;
      buyer_name: string;
      buyer_email?: string;
      buyer_phone?: string;
      buyer_occupation?: string;
      seller_name: string;
      seller_email?: string;
      seller_phone?: string;
      seller_company?: string;
      preferred_lawyer_id: string;
      additional_notes?: string;
    }) => {
      if (!user) throw new Error('Not authenticated');
      if (!dealData.property_id) {
        throw new Error('Choose a listed property before submitting this deal.');
      }
      if (!dealData.preferred_lawyer_id) {
        throw new Error('Assign a lawyer before submitting this deal.');
      }

      const { data: property, error: propertyError } = await supabase
        .from('properties')
        .select('id, title, address, state, lga, property_type, price, seller_name, listed_by, seller_user_id, deleted_at')
        .eq('id', dealData.property_id)
        .maybeSingle();

      if (propertyError) throw propertyError;
      if (!property || property.deleted_at) {
        throw new Error('Choose a valid listed property before submitting this deal.');
      }
      if (!canCreateDealForProperty(property, user.id)) {
        throw new Error('You can only submit deals for properties listed from your workspace.');
      }

      const { data, error } = await supabase
        .from('deals')
        .insert({
          ...dealData,
          property_title: property.title,
          property_address: property.address,
          property_state: property.state,
          property_lga: property.lga,
          property_type: property.property_type,
          estimated_value: property.price,
          seller_name: dealData.seller_name || property.seller_name || 'Seller',
          deal_number: generateClientDealNumber(),
          agent_id: user.id,
          status: 'pending-acceptance',
          submitted_at: new Date().toISOString(),
        })
        .select()
        .single();
      if (error) throw error;

      try {
        await supabase.functions.invoke('create-notification', {
          body: {
            user_id: dealData.preferred_lawyer_id,
            title: 'New Deal Intake',
            message: `A new deal "${dealData.property_title}" has been submitted and assigned to you for review.`,
            type: 'deal',
            link_to: `/deal-intake/${data.id}`,
          },
        });
      } catch {
        // Notification is non-critical
      }

      return data;
    },
    onSuccess: async (data) => {
      await logActivity('deal_submitted', { deal_id: data.id }, data.id, 'deal');
      queryClient.invalidateQueries({ queryKey: ['deals'] });
    },
  });
}

export function useAvailableLawyers() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['profiles', 'available-lawyers'],
    enabled: !!user,
    queryFn: async () => {
      // Use security definer function to get lawyer list without exposing PII
      const { data, error } = await supabase.rpc('get_lawyers_for_selection');
      if (error) throw error;
      return (data || []).map((l: any) => ({
        id: l.id,
        display_name: l.display_name,
        first_name: l.display_name.split(' ')[0] || '',
        last_name: l.display_name.split(' ').slice(1).join(' ') || '',
        organization_name: l.organization_name,
      }));
    },
  });
}
